import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  XMarkIcon,
  LinkIcon,
  ClockIcon,
  TagIcon,
  ChatBubbleLeftIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { Event, EventComment, eventsService } from '../services/events';
import TelegramContextViewer from './TelegramContextViewer';
import { SeverityBadge, StatusBadge, TypeBadge } from './EventBadges';
import toast from 'react-hot-toast';

interface EventDetailsModalProps {
  event: Event | null;
  isOpen: boolean;
  onClose: () => void;
  onEventUpdated?: () => void;
}

const EventDetailsModal: React.FC<EventDetailsModalProps> = ({ event, isOpen, onClose, onEventUpdated }) => {
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [comments, setComments] = useState<EventComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);

  const loadComments = useCallback(async () => {
    if (!event) return;

    setIsLoadingComments(true);
    try {
      const eventComments = await eventsService.getEventComments(event.id);
      setComments(eventComments);
    } catch (error) {
      console.error('Erro ao carregar comentários:', error);
      toast.error('Erro ao carregar comentários');
    } finally {
      setIsLoadingComments(false);
    }
  }, [event]);

  // Carregar comentários quando o modal abrir
  useEffect(() => {
    if (isOpen && event) {
      loadComments();
    }
  }, [isOpen, event, loadComments]);

  if (!event) return null;

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event || !newComment.trim()) return;

    setIsSubmittingComment(true);
    try {
      const comment = await eventsService.createEventComment(event.id, {
        content: newComment.trim(),
        is_internal: false,
      });
      setComments(prev => [...prev, comment]);
      setNewComment('');
      toast.success('Comentário adicionado com sucesso.');
    } catch (error) {
      console.error('Error creating comment:', error);
      toast.error('Erro ao adicionar comentário');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    setIsUpdatingStatus(true);
    try {
      await eventsService.updateEventStatus(event.id, newStatus);
      toast.success('Status do evento atualizado com sucesso!');
      onEventUpdated?.();
      onClose();
    } catch (error) {
      toast.error('Erro ao atualizar status do evento');
      console.error('Error updating event status:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-red-600';
    if (score >= 0.6) return 'text-orange-600';
    if (score >= 0.4) return 'text-yellow-600';
    return 'text-green-600';
  };

  // Fallback para corrigir links de código do GitHub sem "/blob/<branch>/"
  const getGithubBlobUrl = (ev: Event | null): string | undefined => {
    if (!ev) return undefined;
    const url = ev.source_url || '';
    if (ev.source === 'github') {
      if (url && url.includes('/blob/')) return url;
      const repoName = ev.event_metadata?.repository_name;
      const filePath = ev.event_metadata?.file_path;
      if (repoName && filePath) {
        return `https://github.com/${repoName}/blob/main/${String(filePath).replace(/^\//, '')}`;
      }
      const m = /^GitHub Code: ([^/]+\/[^/]+)\/(.+)$/.exec(ev.title || '');
      if (m) {
        return `https://github.com/${m[1]}/blob/main/${m[2]}`;
      }
    }
    return url || undefined;
  };

  const statusOptions = [
    { value: 'new', label: 'Novo' },
    { value: 'processing', label: 'Processando' },
    { value: 'analyzed', label: 'Analisado' },
    { value: 'validated', label: 'Validado' },
    { value: 'false_positive', label: 'Falso Positivo' },
    { value: 'resolved', label: 'Resolvido' },
    { value: 'ignored', label: 'Ignorado' },
  ];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as='div' className='relative z-50' onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black bg-opacity-25' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4 text-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <Dialog.Panel className='w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all'>
                <div className='flex items-center justify-between mb-6'>
                  <Dialog.Title as='h3' className='text-lg font-medium leading-6 text-gray-900 dark:text-gray-100'>
                    Detalhes do Evento
                  </Dialog.Title>
                  <button
                    type='button'
                    className='rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                    onClick={onClose}
                  >
                    <span className='sr-only'>Fechar</span>
                    <XMarkIcon className='h-6 w-6' aria-hidden='true' />
                  </button>
                </div>

                <div className='space-y-6'>
                  {/* Header com título e badges */}
                  <div>
                    <h2 className='text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3'>{event.title}</h2>
                    <div className='flex flex-wrap gap-2 mb-4'>
                      <SeverityBadge severity={event.severity} />
                      <StatusBadge status={event.status} />
                      <TypeBadge type={event.event_type} />
                    </div>
                  </div>

                  {/* Descrição */}
                  {event.description && (
                    <div>
                      <h4 className='text-sm font-medium text-gray-900 dark:text-gray-100 mb-2'>Descrição</h4>
                      <p className='text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg'>
                        {event.description}
                      </p>
                    </div>
                  )}

                  {/* Informações principais */}
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                    {/* Coluna esquerda */}
                    <div className='space-y-4'>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Fonte</h4>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{event.source}</span>
                          {getGithubBlobUrl(event) && event.source !== 'telegram' && (
                            <a
                              href={getGithubBlobUrl(event)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-500"
                            >
                              <LinkIcon className='h-4 w-4' />
                            </a>
                          )}
                        </div>
                      </div>

                      <div>
                        <h4 className='text-sm font-medium text-gray-900 dark:text-gray-100 mb-2'>Pontuações</h4>
                        <div className='space-y-2'>
                          <div className='flex items-center justify-between'>
                            <span className='text-sm text-gray-600 dark:text-gray-400'>Confiança:</span>
                            <span className={`text-sm font-medium ${getScoreColor(event.confidence_score)}`}>
                              {(event.confidence_score * 100).toFixed(1)}%
                            </span>
                          </div>
                          <div className='flex items-center justify-between'>
                            <span className='text-sm text-gray-600 dark:text-gray-400'>Risco:</span>
                            <span className={`text-sm font-medium ${getScoreColor(event.risk_score)}`}>
                              {(event.risk_score * 100).toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Tags */}
                      {event.tags && event.tags.length > 0 && (
                        <div>
                          <h4 className='text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center'>
                            <TagIcon className='h-4 w-4 mr-1' />
                            Tags
                          </h4>
                          <div className='flex flex-wrap gap-1'>
                            {event.tags.map((tag, index) => (
                              <span
                                key={index}
                                className='inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Contexto Telegram */}
                      {event && event.source === 'telegram' && (
                        <div>
                          <TelegramContextViewer event={event as Event} />
                        </div>
                      )}

                      {/* Metadata - Subdomínios */}
                      {event.event_metadata && event.source === 'subdomain_discovery' && (
                        <div>
                          <h4 className='text-sm font-medium text-gray-900 dark:text-gray-100 mb-2'>
                            Subdomínios Descobertos
                          </h4>
                          <div className='bg-gray-50 dark:bg-gray-700 p-3 rounded-lg'>
                            <div className='space-y-2 text-sm'>
                              <div className='flex justify-between'>
                                <span className='text-gray-600 dark:text-gray-400'>Domínio:</span>
                                <span className='text-gray-900 dark:text-gray-100 font-medium'>
                                  {event.event_metadata.domain}
                                </span>
                              </div>
                              <div className='flex justify-between'>
                                <span className='text-gray-600 dark:text-gray-400'>Total encontrados:</span>
                                <span className='text-gray-900 dark:text-gray-100 font-medium'>
                                  {event.event_metadata.total_subdomains}
                                </span>
                              </div>
                              <div className='flex justify-between'>
                                <span className='text-gray-600 dark:text-gray-400'>Método:</span>
                                <span className='text-gray-900 dark:text-gray-100'>
                                  {event.event_metadata.discovery_method}
                                </span>
                              </div>

                              {event.event_metadata.subdomains && event.event_metadata.subdomains.length > 0 && (
                                <div className='mt-3'>
                                  <span className='text-gray-600 dark:text-gray-400 text-xs'>Subdomínios:</span>
                                  <div className='mt-1 max-h-32 overflow-y-auto'>
                                    {event.event_metadata.subdomains
                                      .slice(0, 20)
                                      .map((subdomain: string, index: number) => (
                                        <div
                                          key={index}
                                          className='text-xs text-gray-700 dark:text-gray-300 py-1 px-2 bg-white dark:bg-gray-700 rounded border-l-2 border-blue-200 dark:border-blue-600 mb-1'
                                        >
                                          {subdomain}
                                        </div>
                                      ))}
                                    {event.event_metadata.subdomains.length > 20 && (
                                      <div className='text-xs text-gray-500 dark:text-gray-400 italic mt-2'>
                                        ... e mais {event.event_metadata.subdomains.length - 20} subdomínios
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {event.event_metadata.categories && (
                                <div className='mt-3'>
                                  <span className='text-gray-600 dark:text-gray-400 text-xs'>Categorias:</span>
                                  <div className='mt-1 flex flex-wrap gap-1'>
                                    {Object.entries(event.event_metadata.categories).map(([category, count]) => (
                                      <span
                                        key={category}
                                        className='inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                                      >
                                        {category}: {count as number}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Coluna direita */}
                    <div className='space-y-4'>
                      <div>
                        <h4 className='text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center'>
                          <ClockIcon className='h-4 w-4 mr-1' />
                          Timestamps
                        </h4>
                        <div className='space-y-2 text-sm'>
                          <div className='flex justify-between'>
                            <span className='text-gray-600 dark:text-gray-400'>Criado em:</span>
                            <span className='text-gray-900 dark:text-gray-100'>{formatDate(event.created_at)}</span>
                          </div>
                          {event.updated_at && (
                            <div className='flex justify-between'>
                              <span className='text-gray-600 dark:text-gray-400'>Atualizado em:</span>
                              <span className='text-gray-900 dark:text-gray-100'>{formatDate(event.updated_at)}</span>
                            </div>
                          )}
                          {event.occurred_at && (
                            <div className='flex justify-between'>
                              <span className='text-gray-600 dark:text-gray-400'>Ocorreu em:</span>
                              <span className='text-gray-900 dark:text-gray-100'>{formatDate(event.occurred_at)}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Usuário que tratou o evento */}
                      {event.assigned_to && (
                        <div>
                          <h4 className='text-sm font-medium text-gray-900 dark:text-gray-100 mb-2'>Tratado por</h4>
                          <div className='bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg'>
                            <div className='flex items-center space-x-2'>
                              <div className='w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center'>
                                <span className='text-white text-sm font-medium'>
                                  {event.assigned_to.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className='text-sm font-medium text-gray-900 dark:text-gray-100'>
                                  {event.assigned_to.name}
                                </p>
                                <p className='text-xs text-gray-600 dark:text-gray-400'>{event.assigned_to.email}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Atualizar Status */}
                      <div>
                        <h4 className='text-sm font-medium text-gray-900 dark:text-gray-100 mb-2'>Atualizar Status</h4>
                        <select
                          value={event.status}
                          onChange={e => handleStatusUpdate(e.target.value)}
                          disabled={isUpdatingStatus}
                          className='block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                        >
                          {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Seção de Comentários */}
                  <div className='border-t border-gray-200 dark:border-gray-700 pt-6'>
                    <div className='flex items-center mb-4'>
                      <ChatBubbleLeftIcon className='h-5 w-5 text-gray-400 dark:text-gray-500 mr-2' />
                      <h4 className='text-lg font-medium text-gray-900 dark:text-gray-100'>Comentários</h4>
                      <span className='ml-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs px-2 py-1 rounded-full'>
                        {comments.length}
                      </span>
                    </div>

                    {/* Lista de comentários */}
                    <div className='space-y-4 mb-6'>
                      {isLoadingComments ? (
                        <div className='flex justify-center py-4'>
                          <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600'></div>
                        </div>
                      ) : comments.length === 0 ? (
                        <div className='text-center py-6 text-gray-500 dark:text-gray-400'>
                          <ChatBubbleLeftIcon className='h-12 w-12 mx-auto mb-2 text-gray-300 dark:text-gray-600' />
                          <p>Nenhum comentário ainda</p>
                          <p className='text-sm'>Seja o primeiro a comentar neste evento</p>
                        </div>
                      ) : (
                        <div className='max-h-64 overflow-y-auto space-y-3'>
                          {comments.map(comment => (
                            <div key={comment.id} className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4'>
                              <div className='flex items-start justify-between mb-2'>
                                <div className='flex items-center space-x-2'>
                                  <div className='w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center'>
                                    <span className='text-white text-sm font-medium'>
                                      {comment.author_name.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <p className='text-sm font-medium text-gray-900 dark:text-gray-100'>
                                      {comment.author_name}
                                    </p>
                                    <p className='text-xs text-gray-500 dark:text-gray-400'>
                                      {new Date(comment.created_at).toLocaleString('pt-BR')}
                                    </p>
                                  </div>
                                </div>
                                {comment.is_internal && (
                                  <span className='inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-yellow-100 text-yellow-800'>
                                    Interno
                                  </span>
                                )}
                              </div>
                              <p className='text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap'>
                                {comment.content}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Formulário para novo comentário */}
                    <form onSubmit={handleSubmitComment} className='space-y-3'>
                      <div>
                        <label
                          htmlFor='comment'
                          className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
                        >
                          Adicionar comentário
                        </label>
                        <textarea
                          id='comment'
                          rows={3}
                          value={newComment}
                          onChange={e => setNewComment(e.target.value)}
                          placeholder='Digite seu comentário sobre este evento...'
                          className='block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                          disabled={isSubmittingComment}
                        />
                      </div>
                      <div className='flex justify-between items-center'>
                        <p className='text-xs text-gray-500 dark:text-gray-400'>
                          Este comentário será enviado por email para o cliente
                        </p>
                        <button
                          type='submit'
                          disabled={!newComment.trim() || isSubmittingComment}
                          className='inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                          {isSubmittingComment ? (
                            <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                          ) : (
                            <PaperAirplaneIcon className='h-4 w-4 mr-2' />
                          )}
                          {isSubmittingComment ? 'Enviando...' : 'Enviar'}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Ações */}
                  <div className='flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700'>
                    <button
                      type='button'
                      className='inline-flex justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50'
                      onClick={async () => {
                        if (!event) return;
                        try {
                          setIsCreatingTicket(true);
                          const created = await eventsService.createTicketFromEvent(event.id);
                          toast.success('Ticket criado com sucesso');
                          if (onEventUpdated) onEventUpdated();
                        } catch (e) {
                          console.error(e);
                          toast.error('Falha ao criar ticket');
                        } finally {
                          setIsCreatingTicket(false);
                        }
                      }}
                      disabled={isCreatingTicket}
                    >
                      {isCreatingTicket ? 'Criando...' : 'Criar Ticket'}
                    </button>
                    <button
                      type='button'
                      className='inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                      onClick={onClose}
                    >
                      Fechar
                    </button>
                    {event.source_url && (
                      <a
                        href={event.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        Ver Fonte Original
                      </a>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EventDetailsModal;
